<!--
  A planet, turning: a disc lit from below, a graticule that turns with it and hides behind the
  limb, seams and hotspots glowing through, a night side, and a rim of light. With `orbiters`, motes
  circle it on tilted orbits - the far half of each drawn before the globe and the near half after,
  which is the whole of the depth illusion - and strike its face as they pass.

  `spin` is radians a second, `pitch` and `roll` radians, as the TypeScript globe took them.
-->
<Library>
  <Define name="globe" label="Globe" land="#1a1411" fire="accent" spark="spark(fire)"
          gridColor="alpha(mix(#a07c3a, fire, 0.4), 1)" grid="1" seams="11" hotspots="9"
          spin="0.14" pitch="0.4" roll="-0.3" night="0.55" glow="1" orbiters="0" strikes="1"
          intro="0.9" color1="#ffd36b" color2="#c8e67a" color3="#9fc8ff" color4="#b6ffc4"
          overflow="max(0, min(w, h) / 2 / (orbiters > 0 ? 1.85 : 1.1) * 1.9 - min(w, h) / 2)">
    <Param name="land" label="Land" kind="color"/>
    <Param name="fire" label="Light" kind="color"/>
    <Param name="spark" label="Sparks" kind="color"/>
    <Param name="gridColor" label="Grid color" kind="color"/>
    <Param name="grid" label="Grid lines" kind="boolean"/>
    <Param name="seams" label="Seams" kind="number" min="0" max="30" step="1"/>
    <Param name="hotspots" label="Hotspots" kind="number" min="0" max="30" step="1"/>
    <Param name="spin" label="Spin" kind="number" min="0" max="1" step="0.02"/>
    <Param name="pitch" label="Tilt" kind="number" min="-1.5" max="1.5" step="0.05"/>
    <Param name="roll" label="Roll" kind="number" min="-1.5" max="1.5" step="0.05"/>
    <Param name="night" label="Night side" kind="number" min="0" max="1" step="0.05"/>
    <Param name="glow" label="Glow" kind="number" min="0" max="1" step="0.1"/>
    <Param name="orbiters" label="Orbiters" kind="number" min="0" max="8" step="1"/>
    <Param name="strikes" label="Strikes" kind="boolean"/>
    <Param name="intro" label="Rise-in seconds" kind="number" min="0" max="5" step="0.1"/>
    <Param name="color1" label="Orbiter 1" kind="color"/>
    <Param name="color2" label="Orbiter 2" kind="color"/>
    <Param name="color3" label="Orbiter 3" kind="color"/>
    <Param name="color4" label="Orbiter 4" kind="color"/>
    <Let name="cx" value="w / 2"/>
    <Let name="cy" value="h / 2"/>
    <Let name="R" value="min(w, h) / 2 / (orbiters > 0 ? 1.85 : 1.1)"/>
    <Let name="up" value="(intro > 0 ? smooth((t - 0.2) / intro) : 1) * reveal"/>
    <Let name="g" value="R * (0.85 + 0.15 * up)"/>
    <Let name="breathe" value="0.85 + 0.15 * sin(t * 0.9)"/>
    <Let name="width" value="max(0.6, g / 90)"/>

    <Group visible="up > 0 and R > 0">
      <!-- The far side of every orbit, behind the globe. -->
      <Repeat count="min(12, floor(orbiters))">
        <Let name="reach" value="1.4 + rand(1) * 0.4"/>
        <Let name="incline" value="deg(1.15 + rand(2) * 0.25)"/>
        <Let name="knot" value="deg((rand(3) - 0.5) * 2.6)"/>
        <Let name="speed" value="(0.42 + rand(4) * 0.22) * (i % 2 == 0 ? 1 : -1)"/>
        <Let name="start" value="rand(5) * tau"/>
        <Let name="ink" value="i % 4 == 0 ? color1 : i % 4 == 1 ? color2 : i % 4 == 2 ? color3 : color4"/>
        <Let name="shown" value="(intro > 0 ? smooth((t - start * 0.3 - 1.1) / 0.8) : 1) * up"/>
        <Let name="angle" value="start + t * speed"/>
        <OrbitPath cx="cx" cy="cy" r="R * reach" incline="incline" node="knot" side="back"
                   stroke="alpha(ink, 0.1 * shown)"/>
        <Repeat count="9" as="j">
          <Orbit cx="cx" cy="cy" r="R * reach" incline="incline" node="knot" side="back"
                 angle="deg(angle - sign(speed) * (9 - j) * 0.045)">
            <Circle r="2.2 * (1 - (9 - j) / 11)" fill="alpha(ink, 0.5 * (1 - (9 - j) / 10) * shown)"/>
          </Orbit>
        </Repeat>
        <Orbit cx="cx" cy="cy" r="R * reach" incline="incline" node="knot" side="back" angle="deg(angle)">
          <Circle r="11" fill="radial(alpha(ink, 0.55 * shown), alpha(ink, 0))"/>
          <Circle r="2.6" fill="alpha(mix(ink, #f0e6d2, 0.5), shown)"/>
        </Orbit>
      </Repeat>

      <!-- The globe. -->
      <Circle visible="glow > 0" cx="cx" cy="cy" r="g * 1.9"
              fill="radial(alpha(fire, 0.32 * breathe * glow * up), alpha(fire, 0))"/>
      <Circle cx="cx" cy="cy" r="g" fill="alpha(land, up)"/>
      <Circle cx="cx" cy="cy" r="g"
              fill="radial(alpha(fire, 0.3 * breathe * glow * up), alpha(fire, 0), 0.55, 0.95, 0.9, 0.75)"/>
      <Sphere cx="cx" cy="cy" r="g" turn="deg(t * spin)" pitch="deg(pitch)" roll="deg(roll)">
        <Group visible="grid">
          <Parallels step="30" limit="60" stroke="alpha(gridColor, 0.22 * up)"/>
          <Meridians count="12" stroke="alpha(gridColor, 0.22 * up)"/>
        </Group>
        <Repeat count="seams">
          <Let name="heat" value="0.45 + 0.55 * (0.5 + 0.5 * sin(t * 1.6 + rand(9) * tau))"/>
          <Let name="steps" value="5 + floor(rand(4) * 6)"/>
          <Walk x="deg(rand(2) * tau)" y="deg((rand(1) - 0.5) * 2.4)" heading="deg(rand(3) * tau)"
                steps="steps" turn="deg((rand(5) - 0.5) * 1.1)" step="deg(0.11)" strokeWidth="2.2 * width"
                stroke="alpha(fire, 0.75 * heat * smooth(z * 2.5) * up)"
                glow="alpha(fire, heat * smooth(z * 2.5) * up)" glowSize="5 * width"/>
          <Walk x="deg(rand(2) * tau)" y="deg((rand(1) - 0.5) * 2.4)" heading="deg(rand(3) * tau)"
                steps="steps" turn="deg((rand(5) - 0.5) * 1.1)" step="deg(0.11)" strokeWidth="0.9 * width"
                stroke="alpha(spark, 0.85 * heat * smooth(z * 2.5) * up)"/>
        </Repeat>
        <Repeat count="hotspots">
          <Let name="beat" value="0.5 + 0.5 * sin(t * 2.2 + rand(3) * tau)"/>
          <Circle cx="deg(rand(2) * tau)" cy="deg((rand(1) - 0.5) * 2.6)"
                  r="g * 0.07 * (0.5 + rand(4) * 0.8) * (0.8 + 0.4 * beat)"
                  fill="radial(alpha(spark, 0.7 * smooth(z * 2.5) * beat * up), alpha(fire, 0))"/>
        </Repeat>
      </Sphere>
      <Circle visible="night > 0" cx="cx" cy="cy" r="g"
              fill="radial(alpha(#040508, 0), alpha(#040508, night * up), 0.45, 0.7, 0.85, 0.95)"/>
      <Circle cx="cx" cy="cy" r="g + 3" strokeWidth="6" stroke="alpha(fire, 0.16 * breathe * up)"/>
      <Circle cx="cx" cy="cy" r="g - 0.5" strokeWidth="2"
              stroke="alpha(mix(fire, spark, 0.35), 0.9 * breathe * up)"/>

      <!-- The near side of every orbit, in front, and the strikes. -->
      <Repeat count="min(12, floor(orbiters))">
        <Let name="reach" value="1.4 + rand(1) * 0.4"/>
        <Let name="incline" value="deg(1.15 + rand(2) * 0.25)"/>
        <Let name="knot" value="deg((rand(3) - 0.5) * 2.6)"/>
        <Let name="speed" value="(0.42 + rand(4) * 0.22) * (i % 2 == 0 ? 1 : -1)"/>
        <Let name="start" value="rand(5) * tau"/>
        <Let name="ink" value="i % 4 == 0 ? color1 : i % 4 == 1 ? color2 : i % 4 == 2 ? color3 : color4"/>
        <Let name="shown" value="(intro > 0 ? smooth((t - start * 0.3 - 1.1) / 0.8) : 1) * up"/>
        <Let name="angle" value="start + t * speed"/>
        <OrbitPath cx="cx" cy="cy" r="R * reach" incline="incline" node="knot" side="front"
                   stroke="alpha(ink, 0.22 * shown)"/>
        <Repeat count="9" as="j">
          <Orbit cx="cx" cy="cy" r="R * reach" incline="incline" node="knot" side="front"
                 angle="deg(angle - sign(speed) * (9 - j) * 0.045)">
            <Circle r="2.2 * (1 - (9 - j) / 11)" fill="alpha(ink, 0.5 * (1 - (9 - j) / 10) * shown)"/>
          </Orbit>
        </Repeat>
        <Orbit cx="cx" cy="cy" r="R * reach" incline="incline" node="knot" side="front" angle="deg(angle)">
          <Circle r="11" fill="radial(alpha(ink, 0.55 * shown), alpha(ink, 0))"/>
          <Circle r="2.6" fill="alpha(mix(ink, #f0e6d2, 0.5), shown)"/>
        </Orbit>
        <!-- Seconds since this mote last passed the front of its orbit. -->
        <Let name="since" value="mod(sign(speed) * (angle - pi / 2), tau) / abs(speed)"/>
        <Orbit visible="strikes and t >= 3 and 1 >= since" cx="cx" cy="cy" r="R * reach"
               incline="incline" node="knot" angle="deg(angle - speed * since)">
          <Let name="k" value="R * 0.7 / (R * reach) - 1"/>
          <Let name="bolt" value="1 - since / 0.3"/>
          <Let name="burst" value="(1 - since) * (1 - since)"/>
          <Line visible="0.3 > since" x2="ox * k" y2="oy * k" strokeWidth="5" stroke="alpha(ink, 0.2 * bolt * up)"/>
          <Line visible="0.3 > since" x2="ox * k" y2="oy * k" strokeWidth="1.5" stroke="alpha(#f0e6d2, 0.9 * bolt * up)"/>
          <Circle cx="ox * k" cy="oy * k" r="R * (0.12 + since * 0.35)"
                  fill="radial(alpha(spark, 0.85 * burst * up), alpha(fire, 0))"/>
          <Circle cx="ox * k" cy="oy * k" r="R * (0.12 + since * 0.35) * 0.8" strokeWidth="1.5"
                  stroke="alpha(ink, 0.6 * burst * up)"/>
        </Orbit>
      </Repeat>
    </Group>
  </Define>
</Library>
